<template>
  <div :class="classnames">
    <div class="app-reviews__container container">
      <h2
        class="app-reviews__headline"
        v-html="formattedHeadline" />

      <div class="app-reviews__platforms grid">
        <div class="app-reviews__platform app-reviews__ios">
          <app-platform-reviews-count
            platform="ios" />
        </div>

        <div class="app-reviews__platform app-reviews__android">
          <app-platform-reviews-count
            platform="android" />
        </div>
      </div>

      <div class="grid">
        <div class="app-reviews__user-reviews">
          <user-reviews />
        </div>
      </div>

      <div
        class="main-features__cta flex justify-center"
        v-if="!hideCta">
        <slot name="cta">
          <start-trial-button-with-pricing
            event-category="body-20210610-app_reviews"
            :signup-route="signupRoute"
            :show-trial-end-date="showTrialEndDate"
            :cta-copy="ctaCopy" />
        </slot>
      </div>
    </div>
  </div>
</template>

<script>
import AppPlatformReviewsCount from "@/pages/book/components/app-platform-reviews-count";
import UserReviews from "@/pages/book/components/user-reviews";
import StartTrialButtonWithPricing from "@/shared/start-trial-button-with-pricing";
import globals from "@/utils/globals";
import { mapGetters } from "vuex";
import bemBlockModifiers from "@/utils/bem-block-modifiers";

export default {
  name: "AppReviews",
  components: {
    AppPlatformReviewsCount,
    UserReviews,
    StartTrialButtonWithPricing
  },
  props: {
    signupRoute: {
      type: String,
      required: false,
      default: ""
    },
    showTrialEndDate: {
      type: Boolean,
      default: false
    },
    hideCta: {
      type: Boolean,
      default: false
    },
    ctaCopy: {
      type: String,
      required: false,
      default: null
    },
    modifiers: {
      type: Array,
      default: () => {}
    }
  },
  computed: {
    ...mapGetters("plans", ["plans", "trialDays"]),
    formattedHeadline() {
      return this.$t("headlineHtml", {
        totalUsers: globals.totalUsers
      });
    },
    classnames() {
      return bemBlockModifiers("app-reviews", this.modifiers);
    }
  },
  i18n: {
    messages: {
      en: {
        headlineHtml: "Join <span class='bg-background-purple'>{totalUsers} Million</span> others growing with Blinkist"
      },
      de: {
        headlineHtml: "Bereits über <span class='bg-background-purple'>{totalUsers} Millionen</span> Nutzer"
      }
    }
  }
};
</script>

<style lang="scss">
.app-reviews {

  /* stylelint-disable-next-line selector-nested-pattern */
  &:not(&--pt-0) {
    padding: $spacing-48 0;
  }

  &__headline { /* stylelint-disable-line selector-nested-pattern */
    @include font-style((xs: h3, m: h2));

    @include font-family(sans-serif);

    text-align: center;

    mark { /* stylelint-disable-line selector-nested-pattern */
      @include highlight;
    }
  }

  &__platforms {
    margin-bottom: $spacing-48;
  }

  &__platform {

    &:first-child {
      margin-bottom: $spacing-24;
    }

    @include media(min-width, m) {

      &:first-child {
        margin-bottom: 0;
      }
    }
  }

  &__user-reviews {
    @include grid-css-element(12);

    margin-bottom: $spacing-32;

    @include media(min-width, m) {
      @include grid-css-element(8, 3);
    }
  }

  &__ios {
    @include grid-css-element(12);

    @include media(min-width, m) {
      @include grid-css-element(5, 2);
    }
  }

  &__android {
    @include grid-css-element(12);

    @include media(min-width, m) {
      @include grid-css-element(5, 7);
    }
  }

  &__cta {
    margin-bottom: $spacing-16;

    text-align: center;
  }

  &__trial-info {
    @include font-style(caption);

    text-align: center;
  }

  @include media(min-width, m) {
    padding: 0 0 $spacing-64;

    &:not(&--pt-0) {
      padding: $spacing-64 0;
    }
  }
}
</style>
